import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Work as WorkIcon,
  Add as AddIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import apiService from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, getAuthToken } = useAuth();
  const { config } = useConfig();
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalCandidates: 0,
    recentMatches: 0,
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (config && getAuthToken) {
      apiService.initialize(config.apiGatewayUrl, getAuthToken);
      loadDashboardData();
    }
  }, [config, getAuthToken]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const jobs = await apiService.getJobs();
      
      // Calculate stats from actual job data structure
      const totalJobs = jobs.length;
      const activeJobs = jobs.filter(job => job.status === 'active').length;
      
      setStats({
        totalJobs,
        activeJobs,
        totalCandidates: jobs.reduce((total, job) => {
          return total + (job.candidates ? job.candidates.length : 0);
        }, 0),
        recentMatches: 0, // This would need to be calculated from recent activity
      });
      
      // Get recent jobs (last 5) and ensure they have the expected structure
      const recentJobsData = jobs.slice(0, 5).map(job => ({
        jobId: job.jobId,
        title: job.title || 'Untitled Job',
        department: job.department || 'No department specified',
        location: job.location || 'Remote',
        status: job.status || 'active',
        createdAt: job.created_at || job.posted,
        skills: job.skills || []
      }));
      
      setRecentJobs(recentJobsData);
      
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'Create New Job',
      description: 'Post a new job opening',
      icon: <AddIcon />,
      color: 'primary',
      action: () => navigate('/create-job'),
    },
    {
      title: 'View All Jobs',
      description: 'Browse all job postings',
      icon: <WorkIcon />,
      color: 'secondary',
      action: () => navigate('/jobs'),
    },
    {
      title: 'Manage Resumes',
      description: 'Upload and organize candidate resumes',
      icon: <PersonIcon />,
      color: 'info',
      action: () => navigate('/resume-management'),
    },
    {
      title: 'AI Candidate Matching',
      description: 'Find best candidates with AI analysis',
      icon: <PeopleIcon />,
      color: 'success',
      action: () => navigate('/candidate-matching'),
    },
  ];

  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={`${color}.main`}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Box display="flex" alignItems="center" mb={2}>
          <DashboardIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Dashboard
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Welcome back, {user?.username}! Here's an overview of your recruitment activities.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Jobs"
            value={stats.totalJobs}
            icon={<WorkIcon sx={{ fontSize: 40 }} />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Jobs"
            value={stats.activeJobs}
            icon={<TrendingUpIcon sx={{ fontSize: 40 }} />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Candidates"
            value={stats.totalCandidates}
            icon={<PeopleIcon sx={{ fontSize: 40 }} />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Recent Matches"
            value={stats.recentMatches}
            icon={<AssessmentIcon sx={{ fontSize: 40 }} />}
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                {quickActions.map((action, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    startIcon={action.icon}
                    onClick={action.action}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      p: 2,
                      borderColor: `${action.color}.main`,
                      color: `${action.color}.main`,
                      '&:hover': {
                        borderColor: `${action.color}.dark`,
                        backgroundColor: `${action.color}.50`,
                      },
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" component="div">
                        {action.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {action.description}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Jobs */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Jobs
              </Typography>
              {recentJobs.length > 0 ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  {recentJobs.map((job, index) => (
                    <Box
                      key={job.jobId || index}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <Typography variant="subtitle1" component="div">
                        {job.title || 'Untitled Job'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {job.department || 'No department specified'}
                      </Typography>
                      <Box display="flex" gap={1} mt={1}>
                        <Chip
                          label={job.status || 'active'}
                          size="small"
                          color={job.status === 'active' ? 'success' : 'default'}
                        />
                        <Chip
                          label={job.location || 'Remote'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No jobs found. Create your first job to get started!
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
